import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { HowItWorks } from "@/components/HowItWorks";
import { Features } from "@/components/Features";
import { Cta } from "@/components/Cta";
import { ProblemSolution } from "@/components/ProblemSolution";
import { Testimonials } from "@/components/Testimonials";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useAnalytics } from "@/hooks/useAnalytics";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useTranslation } from "react-i18next";

const Index = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const { trackPageView, trackAction } = useAnalytics({ component: "Index", pageView: true });

  useEffect(() => {
    // Track page view
    trackPageView("index_page");
    
    // Check if there are auth-related parameters in the URL
    const hasAuthParams = searchParams.has("access_token") || 
                          searchParams.has("refresh_token") || 
                          searchParams.has("provider_token") || 
                          searchParams.has("code");

    if (hasAuthParams) {
      console.log("Detected auth params in URL on Index page, redirecting to auth/callback for handling");
      navigate("/auth/callback" + window.location.search, { replace: true });
      return;
    }

    // Regular session check
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log("Found existing session on Index page, checking user profile");
          // Check if the user has completed onboarding
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('profession')
            .eq('id', session.user.id)
            .single();
            
          if (profile && profile.profession) {
            console.log("User has profession set, redirecting to dashboard");
            navigate('/dashboard', { replace: true });
          } else {
            console.log("User missing profession, redirecting to simple-onboarding");
            navigate('/simple-onboarding', { replace: true });
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error checking session:", error);
        setIsLoading(false);
      }
    };

    checkAuth();

    // No need for auth state change listener on index page
    // since we already check the session on page load
    // and we'll handle auth changes in the Header component
  }, [navigate, searchParams, trackPageView]);

  const navigateToAuth = (defaultTab: 'signin' | 'signup') => {
    // Track the action
    trackAction(`header_${defaultTab}_click`);
    
    // Get UTM parameters from URL
    const utm_source = searchParams.get("utm_source") || "";
    const utm_medium = searchParams.get("utm_medium") || "";
    const utm_campaign = searchParams.get("utm_campaign") || "";
    
    // Build the navigation URL with UTM parameters
    const authUrl = new URL("/auth", window.location.origin);
    authUrl.searchParams.append("tab", defaultTab);
    
    // Only add UTM parameters if they exist
    if (utm_source) authUrl.searchParams.append("utm_source", utm_source);
    if (utm_medium) authUrl.searchParams.append("utm_medium", utm_medium);
    if (utm_campaign) authUrl.searchParams.append("utm_campaign", utm_campaign);
    
    navigate(authUrl.pathname + authUrl.search);
  };

  if (isLoading) {
    // Show a simple loading state
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gebeya-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b landing-page-header">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img 
                src="/lovable-uploads/bc4b57d4-e29b-4e44-8e1c-82ec09ca6fd6.png" 
                alt="Gebeya" 
                className="h-6 sm:h-8 flex-shrink-0"
              />
              <div className="flex items-center min-w-0">
                <span className="font-bold text-lg sm:text-xl bg-gradient-to-r from-gebeya-pink to-gebeya-orange bg-clip-text text-transparent">Jitume</span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageToggle />
              <Button 
                variant="outline" 
                className="hidden md:inline-flex"
                onClick={() => navigateToAuth('signin')}
              >
                {t('auth.signIn')}
              </Button>
              <Button 
                className="hidden md:inline-flex bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white"
                onClick={() => navigateToAuth('signup')}
              >
                {t('auth.getStarted')}
              </Button>
              {/* Mobile: Show only Sign In */}
              <Button 
                size="sm"
                className="inline-flex md:hidden bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white"
                onClick={() => navigateToAuth('signin')}
              >
                {t('auth.signIn')}
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="relative z-10">
        <Hero />
        <ProblemSolution />
        <HowItWorks />
        <Testimonials />
        <Features />
        <Cta />
      </main>
    </div>
  );
};

export default Index;
